import { Link } from '#app/components/Link/index.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import {
  Card,
  CreateButton,
  Description,
  Links,
  NativeEmoji,
  Title,
  TitleArea,
} from './CreateCard.styles.js';
import { LightbulbEmoji, SparklesEmoji } from '@contra/icons/emojis';
import { AddIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { type MouseEvent, type ReactNode, useCallback } from 'react';

type CreateCardVariant =
  | 'client-service'
  | 'client-work'
  | 'invite-teammates'
  | 'job'
  | 'notes'
  | 'opportunity'
  | 'project'
  | 'recommendations'
  | 'send-an-invoice'
  | 'service'
  | 'start-project';

const getVariantDetails = ({
  displayUsername,
  isClient,
  variant,
}: {
  displayUsername: string;
  isClient: boolean;
  variant: CreateCardVariant;
}): {
  description: string;
  helpLinks: ReadonlyArray<{
    emoji?: ReactNode;
    hide?: boolean;
    label: string;
    sameWindow?: boolean;
    url: string;
  }>;
  onClick?: () => void;
  title: string;
  to?: string;
} => {
  switch (variant) {
    case 'client-service':
      return {
        description:
          'Advertise your skills to support the freelancer community.',
        helpLinks: [
          {
            emoji: <LightbulbEmoji size={16} />,
            label: 'Guide To Services',
            url: getRoute('/p/@slug', {
              args: { slug: 'ZNbl3PUR-guide-to-services' },
            }),
          },
          {
            label: 'Import content in seconds',
            url: getRoute('/p/@slug', {
              args: {
                slug: 'XEWX2APc-improving-your-contra-profile',
              },
            }),
          },
        ],
        title: 'Create a Service',
        to: getRoute('/service/new'),
      };
    case 'client-work':
    case 'project':
      return {
        description:
          'Your projects should highlight your best skills and experience.',
        helpLinks: [
          {
            emoji: <SparklesEmoji size={16} />,
            hide: true,
            label: 'Generate AI suggestions',
            sameWindow: true,
            url: getRoute('/@username/projects', {
              args: { username: displayUsername },
              search: {
                profileMagic: true,
              },
            }),
          },
          {
            emoji: (
              <NativeEmoji
                aria-label="Import content in seconds"
                role="img"
              >
                🪄
              </NativeEmoji>
            ),
            label: 'Import content in seconds',
            sameWindow: true,
            url: getRoute('/project/new', {
              search: {
                importContent: true,
              },
            }),
          },
        ].filter(({ hide }) => !hide),
        onClick: undefined,
        title: 'Add a project',
        to: getRoute('/project/new'),
      };
    case 'invite-teammates':
      return {
        description:
          'Collaborate on projects with a shared network of your team’s top freelance talent',
        helpLinks: [],
        title: 'Invite teammates',
      };
    case 'job':
      return {
        description:
          'Match with top contractors to collaborate on your next project.',
        helpLinks: [],
        title: 'Post A Job',
        to: getRoute('/jobs/new'),
      };
    case 'notes':
      return {
        description: '',
        helpLinks: [],
        title: 'Internal note',
      };

    case 'opportunity':
      return {
        description:
          'Match with top contractors to collaborate on your next project.',
        helpLinks: [
          {
            emoji: <LightbulbEmoji size={16} />,
            label: 'How to Post a Top-Tier Job',
            url: getRoute('/p/@slug', {
              args: {
                slug: 'BEQORqcN-how-to-post-a-top-tier-opportunity',
              },
            }),
          },
          {
            label: 'How to Hire on Contra',
            url: getRoute('/p/@slug', {
              args: { slug: 'PesUUPlo-how-to-hire-on-contra' },
            }),
          },
        ],
        title: 'Post A Job',
        to: getRoute('/jobs/new'),
      };

    case 'recommendations':
      return {
        description:
          'Build your reputation from past clients and collaborators',
        helpLinks: [
          {
            emoji: <LightbulbEmoji size={16} />,
            label: 'Recommendations on Contra',
            url: getRoute('/p/@slug', {
              args: {
                slug: 'Qk77kOsm-boost-your-reputation-with-recommendations',
              },
            }),
          },
        ],
        title: 'Request recommendation',
        to: getRoute('/independent/request-recommendation'),
      };
    case 'send-an-invoice':
      return {
        description: isClient
          ? 'Request a one time invoice for a new or existing project'
          : 'Create a one time invoice for a new or existing project',
        helpLinks: [],
        title: isClient ? 'Request Invoice' : 'Send an Invoice',
      };

    case 'service':
      return {
        description: "The clearer the service, the faster you'll get hired.",
        helpLinks: [
          {
            emoji: <SparklesEmoji size={16} />,
            hide: true,
            label: 'Generate AI suggestions',
            sameWindow: true,
            url: getRoute('/@username/services', {
              args: { username: displayUsername },
              search: {
                profileMagic: true,
              },
            }),
          },
        ].filter(({ hide }) => !hide),
        onClick: undefined,
        title: 'Add a service',
        to: getRoute('/service/new'),
      };

    case 'start-project':
      return {
        description: "Start a project with a contractor you're ready to hire",
        helpLinks: [],
        title: 'Start project',
      };
    default:
      throw new Error(`Unhandled create card variant: ${variant}`);
  }
};

type CreateCardProps = {
  readonly onCreateClick?: (event: MouseEvent) => void;
  readonly variant: CreateCardVariant;
};

export const CreateCard = ({ onCreateClick, variant }: CreateCardProps) => {
  const { track } = useAnalytics();
  const { push } = useNavigation();
  const { isClient } = useSelectedUserType();

  const onCreateCardViewedRef = useOnElementViewed<HTMLDivElement>(() =>
    track('empty_state', {
      action: 'viewed',
      empty_state_name: 'create_new_asset',
      section: variant,
    }),
  );

  const currentUser = useCurrentUser();

  const onTrackClick = useCallback(
    (cta: string) => {
      track('empty_state', {
        action: 'tapped',
        cta,
        empty_state_name: 'create_new_asset',
        section: variant,
      });
    },
    [track, variant],
  );

  const { description, helpLinks, onClick, title, to } = getVariantDetails({
    displayUsername: currentUser?.profile.displayUsername ?? 'me',
    isClient,
    variant,
  });

  const handleTitleClick = useCallback(
    (event: MouseEvent) => {
      onTrackClick('title');
      onCreateClick?.(event);
    },
    [onCreateClick, onTrackClick],
  );

  const handlePlusButtonClick = useCallback(
    (event: MouseEvent) => {
      onTrackClick('add_button');
      onCreateClick?.(event);

      if (onClick) {
        onClick();
      } else if (!event.defaultPrevented && to) {
        push(to);
      }
    },
    [onClick, onCreateClick, onTrackClick, push, to],
  );

  if (!currentUser) {
    return null;
  }

  return (
    <Card ref={onCreateCardViewedRef}>
      <CreateButton
        aria-label={`${title} icon`}
        onClick={handlePlusButtonClick}
      >
        <div>
          <AddIcon />
        </div>
      </CreateButton>

      <TitleArea>
        <Title
          as={to ? Link : 'button'}
          color="black"
          onClick={handleTitleClick}
          textStyle="fifthLevelHeadline"
          to={to}
        >
          {title}
        </Title>
      </TitleArea>

      {description ? (
        <Description
          color="gray70"
          textStyle="bodyRegular"
        >
          {description}
        </Description>
      ) : null}

      {helpLinks.length > 0 ? (
        <Links>
          {helpLinks.map(({ emoji, label, sameWindow, url }) => (
            <li key={url}>
              {emoji}
              <a
                href={url}
                onClick={() => {
                  onTrackClick('help');
                }}
                rel={sameWindow ? undefined : 'noreferrer'}
                target={sameWindow ? undefined : '_blank'}
              >
                {label}
              </a>
            </li>
          ))}
        </Links>
      ) : null}
    </Card>
  );
};
